import React, { useState, useEffect } from 'react';
import './ManageDRPCUsers.css';
import collegeConfig from '../../config/collegeConfig';
import {
  FaUserMd,
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaSearch,
  FaHospital,
  FaMapMarkerAlt,
  FaPhone,
  FaEnvelope,
  FaTimes,
  FaCheckCircle,
  FaTimesCircle
} from 'react-icons/fa';

const ManageDRPCUsers = () => {
  const [drpcUsers, setDrpcUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    profile: {
      firstName: '',
      lastName: '',
      mobile: ''
    },
    drpcDetails: {
      district: '',
      state: '',
      hospitalName: '',
      designation: 'District Residency Programme Coordinator',
      contactNumber: '',
      assignedDistricts: []
    }
  });

  // Statistics
  const [stats, setStats] = useState({
    totalDRPC: 0,
    activeDRPC: 0,
    districts: 0
  });

  useEffect(() => {
    fetchDRPCUsers();
  }, []);

  const fetchDRPCUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${collegeConfig.api.baseURL}/students/all-users?role=drpc`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        const users = data.users || [];
        setDrpcUsers(users);
        
        // Calculate statistics
        const uniqueDistricts = new Set();
        users.forEach(user => {
          if (user.drpcDetails?.district) {
            uniqueDistricts.add(user.drpcDetails.district);
          }
          if (user.drpcDetails?.assignedDistricts) {
            user.drpcDetails.assignedDistricts.forEach(d => uniqueDistricts.add(d));
          }
        });

        setStats({
          totalDRPC: users.length,
          activeDRPC: users.filter(u => u.isActive).length,
          districts: uniqueDistricts.size
        });
      } else {
        setError('Failed to fetch DRPC users');
      }
    } catch (error) {
      console.error('Error fetching DRPC users:', error);
      setError('Error loading DRPC users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      
      // Prepare data
      const userData = {
        name: `${formData.profile.firstName} ${formData.profile.lastName}`.trim(),
        email: formData.email,
        password: formData.password,
        role: 'drpc',
        profile: {
          firstName: formData.profile.firstName,
          lastName: formData.profile.lastName,
          mobile: formData.profile.mobile
        },
        drpcDetails: {
          district: formData.drpcDetails.district,
          state: formData.drpcDetails.state,
          hospitalName: formData.drpcDetails.hospitalName,
          designation: formData.drpcDetails.designation,
          contactNumber: formData.drpcDetails.contactNumber,
          assignedDistricts: formData.drpcDetails.assignedDistricts.length > 0 
            ? formData.drpcDetails.assignedDistricts 
            : [formData.drpcDetails.district]
        }
      };

        const response = await fetch(`${collegeConfig.api.baseURL}/students/create-user`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(userData)
      });

      if (response.ok) {
        alert('✅ DRPC user created successfully!');
        setShowCreateModal(false);
        resetForm();
        fetchDRPCUsers();
      } else {
        const data = await response.json();
        alert('❌ ' + (data.message || 'Failed to create DRPC user'));
      }
    } catch (error) {
      console.error('Error creating DRPC user:', error);
      alert('❌ Error creating DRPC user');
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      
      // Prepare update data (without password if not changed)
      const updateData = {
        name: `${formData.profile.firstName} ${formData.profile.lastName}`.trim(),
        email: formData.email,
        profile: {
          firstName: formData.profile.firstName,
          lastName: formData.profile.lastName,
          mobile: formData.profile.mobile
        },
        drpcDetails: {
          district: formData.drpcDetails.district,
          state: formData.drpcDetails.state,
          hospitalName: formData.drpcDetails.hospitalName,
          designation: formData.drpcDetails.designation,
          contactNumber: formData.drpcDetails.contactNumber,
          assignedDistricts: formData.drpcDetails.assignedDistricts.length > 0 
            ? formData.drpcDetails.assignedDistricts 
            : [formData.drpcDetails.district]
        }
      };

      // Add password only if provided
      if (formData.password) {
        updateData.password = formData.password;
      }

      const response = await fetch(`${collegeConfig.api.baseURL}/students/update-user/${selectedUser._id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        alert('✅ DRPC user updated successfully!');
        setShowEditModal(false);
        setSelectedUser(null);
        resetForm();
        fetchDRPCUsers();
      } else {
        const data = await response.json();
        alert('❌ ' + (data.message || 'Failed to update DRPC user'));
      }
    } catch (error) {
      console.error('Error updating DRPC user:', error);
      alert('❌ Error updating DRPC user');
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete DRPC user "${userName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${collegeConfig.api.baseURL}/students/delete-user/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        alert('✅ DRPC user deleted successfully');
        fetchDRPCUsers();
      } else {
        alert('❌ Failed to delete DRPC user');
      }
    } catch (error) {
      console.error('Error deleting DRPC user:', error);
      alert('❌ Error deleting DRPC user');
    }
  };

  const handleToggleStatus = async (userId, currentStatus) => {
    const action = currentStatus ? 'deactivate' : 'activate';
    if (!window.confirm(`Are you sure you want to ${action} this DRPC user?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${collegeConfig.api.baseURL}/students/update-user/${userId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ isActive: !currentStatus })
      });

      if (response.ok) {
        alert(`✅ DRPC user ${action}d successfully`);
        fetchDRPCUsers();
      } else {
        alert(`❌ Failed to ${action} DRPC user`);
      }
    } catch (error) {
      console.error(`Error ${action}ing DRPC user:`, error);
      alert(`❌ Error ${action}ing DRPC user`);
    }
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setFormData({
      name: user.name || '',
      email: user.email || '',
      password: '', // Don't populate password
      profile: {
        firstName: user.profile?.firstName || '',
        lastName: user.profile?.lastName || '',
        mobile: user.profile?.mobile || ''
      },
      drpcDetails: {
        district: user.drpcDetails?.district || '',
        state: user.drpcDetails?.state || '',
        hospitalName: user.drpcDetails?.hospitalName || '',
        designation: user.drpcDetails?.designation || 'District Residency Programme Coordinator',
        contactNumber: user.drpcDetails?.contactNumber || '',
        assignedDistricts: user.drpcDetails?.assignedDistricts || []
      }
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      profile: {
        firstName: '',
        lastName: '',
        mobile: ''
      },
      drpcDetails: {
        district: '',
        state: '',
        hospitalName: '',
        designation: 'District Residency Programme Coordinator',
        contactNumber: '',
        assignedDistricts: []
      }
    });
  };

  const handleDistrictsChange = (value) => {
    const districts = value.split(',').map(d => d.trim()).filter(d => d);
    setFormData({
      ...formData,
      drpcDetails: {
        ...formData.drpcDetails,
        assignedDistricts: districts
      }
    });
  };

  const filteredUsers = drpcUsers.filter(user => {
    const searchLower = searchTerm.toLowerCase();
    return (
      user.name?.toLowerCase().includes(searchLower) ||
      user.email?.toLowerCase().includes(searchLower) ||
      user.drpcDetails?.district?.toLowerCase().includes(searchLower) ||
      user.drpcDetails?.hospitalName?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="manage-drpc-container_224">
      {/* Header */}
      <div className="drpc-header_224">
        <h2 className="drpc-title_224">Manage DRPC Users</h2>
        <button className="btn-primary_224" onClick={() => setShowCreateModal(true)}>
          <FaPlus /> Add DRPC User
        </button>
      </div>

      {/* Statistics Cards */}
      <div className="stats-grid_224">
        <div className="stat-card_224" style={{ '--card-color': '#3498DB' }}>
          <div className="stat-icon_224">
            <FaUserMd />
          </div>
          <div className="stat-content_224">
            <h3 className="stat-value_224">{stats.totalDRPC}</h3>
            <p className="stat-label_224">Total DRPC Users</p>
          </div>
        </div>

        <div className="stat-card_224" style={{ '--card-color': '#2ECC71' }}>
          <div className="stat-icon_224">
            <FaCheckCircle />
          </div>
          <div className="stat-content_224">
            <h3 className="stat-value_224">{stats.activeDRPC}</h3>
            <p className="stat-label_224">Active Users</p>
          </div>
        </div>

        <div className="stat-card_224" style={{ '--card-color': '#E67E22' }}>
          <div className="stat-icon_224">
            <FaMapMarkerAlt />
          </div>
          <div className="stat-content_224">
            <h3 className="stat-value_224">{stats.districts}</h3>
            <p className="stat-label_224">Districts Covered</p>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="search-container_224">
        <div className="search-box_224">
          <FaSearch className="search-icon_224" />
          <input
            type="text"
            className="search-input_224"
            placeholder="Search by name, email, district, or hospital..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Users Table */}
      {loading ? (
        <div className="loading-state_224">
          <div className="spinner_224"></div>
          <p>Loading DRPC users...</p>
        </div>
      ) : error ? (
        <div className="error-state_224">{error}</div>
      ) : filteredUsers.length === 0 ? (
        <div className="empty-state_224">
          <FaUserMd size={60} color="#ccc" />
          <h3>No DRPC Users Found</h3>
          <p>Add your first DRPC user to get started</p>
          <button className="btn-primary_224" onClick={() => setShowCreateModal(true)}>
            <FaPlus /> Add DRPC User
          </button>
        </div>
      ) : (
        <div className="users-table-container_224">
          <table className="users-table_224">
            <thead>
              <tr>
                <th>Name</th>
                <th>Contact</th>
                <th>District & Hospital</th>
                <th>Assigned Districts</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => (
                <tr key={user._id}>
                  <td>
                    <div className="user-info_224">
                      <div className="user-avatar_224">
                        {user.profile?.firstName?.charAt(0)}
                        {user.profile?.lastName?.charAt(0)}
                      </div>
                      <div>
                        <strong className="user-name_224">{user.name}</strong>
                        <small className="user-designation_224">
                          {user.drpcDetails?.designation || 'DRPC'}
                        </small>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="contact-info_224">
                      <div className="contact-row_224">
                        <FaEnvelope className="contact-icon_224" />
                        <span>{user.email}</span>
                      </div>
                      {user.drpcDetails?.contactNumber && (
                        <div className="contact-row_224">
                          <FaPhone className="contact-icon_224" />
                          <span>{user.drpcDetails.contactNumber}</span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="location-info_224">
                      <div className="location-row_224">
                        <FaMapMarkerAlt className="location-icon_224" />
                        <span>
                          {user.drpcDetails?.district}, {user.drpcDetails?.state}
                        </span>
                      </div>
                      <div className="location-row_224">
                        <FaHospital className="location-icon_224" />
                        <span>{user.drpcDetails?.hospitalName}</span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="districts-list_224">
                      {user.drpcDetails?.assignedDistricts?.map((district, idx) => (
                        <span key={idx} className="district-badge_224">
                          {district}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td>
                    <button
                      className={`status-badge_224 ${user.isActive ? 'active_224' : 'inactive_224'}`}
                      onClick={() => handleToggleStatus(user._id, user.isActive)}
                      title={`Click to ${user.isActive ? 'deactivate' : 'activate'}`}
                    >
                      {user.isActive ? (
                        <>
                          <FaCheckCircle /> Active
                        </>
                      ) : (
                        <>
                          <FaTimesCircle /> Inactive
                        </>
                      )}
                    </button>
                  </td>
                  <td>
                    <div className="action-buttons_224">
                      <button
                        className="btn-icon_224 btn-view_224"
                        title="View Details"
                        onClick={() => {
                          setSelectedUser(user);
                          setShowViewModal(true);
                        }}
                      >
                        <FaEye />
                      </button>
                      <button
                        className="btn-icon_224 btn-edit_224"
                        title="Edit User"
                        onClick={() => openEditModal(user)}
                      >
                        <FaEdit />
                      </button>
                      <button
                        className="btn-icon_224 btn-delete_224"
                        title="Delete User"
                        onClick={() => handleDeleteUser(user._id, user.name)}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="modal-overlay_224" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content_224" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header_224">
              <h3>Add New DRPC User</h3>
              <button className="close-btn_224" onClick={() => setShowCreateModal(false)}>
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleCreateUser} className="drpc-form_224">
              <div className="form-section_224">
                <h4 className="section-title_224">Personal Information</h4>
                
                <div className="form-row_224">
                  <div className="form-group_224">
                    <label>First Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.profile.firstName}
                      onChange={(e) => setFormData({
                        ...formData,
                        profile: {...formData.profile, firstName: e.target.value}
                      })}
                      placeholder="Dr. Rajesh"
                    />
                  </div>

                  <div className="form-group_224">
                    <label>Last Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.profile.lastName}
                      onChange={(e) => setFormData({
                        ...formData,
                        profile: {...formData.profile, lastName: e.target.value}
                      })}
                      placeholder="Kumar"
                    />
                  </div>
                </div>

                <div className="form-row_224">
                  <div className="form-group_224">
                    <label>Email *</label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="drpc.pune@hospital.gov.in"
                    />
                  </div>

                  <div className="form-group_224">
                    <label>Password *</label>
                    <input
                      type="password"
                      required
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      placeholder="Secure password"
                      minLength="6"
                    />
                  </div>
                </div>

                <div className="form-group_224">
                  <label>Mobile Number</label>
                  <input
                    type="tel"
                    value={formData.profile.mobile}
                    onChange={(e) => setFormData({
                      ...formData,
                      profile: {...formData.profile, mobile: e.target.value}
                    })}
                    placeholder="+91-9876543210"
                  />
                </div>
              </div>

              <div className="form-section_224">
                <h4 className="section-title_224">District & Hospital Details</h4>
                
                <div className="form-row_224">
                  <div className="form-group_224">
                    <label>Primary District *</label>
                    <input
                      type="text"
                      required
                      value={formData.drpcDetails.district}
                      onChange={(e) => setFormData({
                        ...formData,
                        drpcDetails: {...formData.drpcDetails, district: e.target.value}
                      })}
                      placeholder="e.g., Pune"
                    />
                  </div>

                  <div className="form-group_224">
                    <label>State *</label>
                    <input
                      type="text"
                      required
                      value={formData.drpcDetails.state}
                      onChange={(e) => setFormData({
                        ...formData,
                        drpcDetails: {...formData.drpcDetails, state: e.target.value}
                      })}
                      placeholder="e.g., Maharashtra"
                    />
                  </div>
                </div>

                <div className="form-group_224">
                  <label>Hospital Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.drpcDetails.hospitalName}
                    onChange={(e) => setFormData({
                      ...formData,
                      drpcDetails: {...formData.drpcDetails, hospitalName: e.target.value}
                    })}
                    placeholder="e.g., District Hospital Pune"
                  />
                </div>

                <div className="form-group_224">
                  <label>Designation</label>
                  <input
                    type="text"
                    value={formData.drpcDetails.designation}
                    onChange={(e) => setFormData({
                      ...formData,
                      drpcDetails: {...formData.drpcDetails, designation: e.target.value}
                    })}
                    placeholder="District Residency Programme Coordinator"
                  />
                </div>

                <div className="form-group_224">
                  <label>Contact Number</label>
                  <input
                    type="tel"
                    value={formData.drpcDetails.contactNumber}
                    onChange={(e) => setFormData({
                      ...formData,
                      drpcDetails: {...formData.drpcDetails, contactNumber: e.target.value}
                    })}
                    placeholder="+91-9876543210"
                  />
                </div>

                <div className="form-group_224">
                  <label>Assigned Districts (comma-separated)</label>
                  <input
                    type="text"
                    value={formData.drpcDetails.assignedDistricts.join(', ')}
                    onChange={(e) => handleDistrictsChange(e.target.value)}
                    placeholder="Pune, Pimpri-Chinchwad, Satara"
                  />
                  <small className="form-hint_224">
                    Enter multiple districts if this DRPC manages more than one. Leave blank to use primary district only.
                  </small>
                </div>
              </div>

              <div className="modal-actions_224">
                <button
                  type="button"
                  className="btn-secondary_224"
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary_224">
                  <FaPlus /> Create DRPC User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedUser && (
        <div className="modal-overlay_224" onClick={() => setShowEditModal(false)}>
          <div className="modal-content_224" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header_224">
              <h3>Edit DRPC User</h3>
              <button className="close-btn_224" onClick={() => setShowEditModal(false)}>
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleUpdateUser} className="drpc-form_224">
              <div className="form-section_224">
                <h4 className="section-title_224">Personal Information</h4>
                
                <div className="form-row_224">
                  <div className="form-group_224">
                    <label>First Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.profile.firstName}
                      onChange={(e) => setFormData({
                        ...formData,
                        profile: {...formData.profile, firstName: e.target.value}
                      })}
                    />
                  </div>

                  <div className="form-group_224">
                    <label>Last Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.profile.lastName}
                      onChange={(e) => setFormData({
                        ...formData,
                        profile: {...formData.profile, lastName: e.target.value}
                      })}
                    />
                  </div>
                </div>

                <div className="form-row_224">
                  <div className="form-group_224">
                    <label>Email *</label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                    />
                  </div>

                  <div className="form-group_224">
                    <label>New Password (leave blank to keep current)</label>
                    <input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      placeholder="Enter new password to change"
                      minLength="6"
                    />
                  </div>
                </div>

                <div className="form-group_224">
                  <label>Mobile Number</label>
                  <input
                    type="tel"
                    value={formData.profile.mobile}
                    onChange={(e) => setFormData({
                      ...formData,
                      profile: {...formData.profile, mobile: e.target.value}
                    })}
                  />
                </div>
              </div>

              <div className="form-section_224">
                <h4 className="section-title_224">District & Hospital Details</h4>
                
                <div className="form-row_224">
                  <div className="form-group_224">
                    <label>Primary District *</label>
                    <input
                      type="text"
                      required
                      value={formData.drpcDetails.district}
                      onChange={(e) => setFormData({
                        ...formData,
                        drpcDetails: {...formData.drpcDetails, district: e.target.value}
                      })}
                    />
                  </div>

                  <div className="form-group_224">
                    <label>State *</label>
                    <input
                      type="text"
                      required
                      value={formData.drpcDetails.state}
                      onChange={(e) => setFormData({
                        ...formData,
                        drpcDetails: {...formData.drpcDetails, state: e.target.value}
                      })}
                    />
                  </div>
                </div>

                <div className="form-group_224">
                  <label>Hospital Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.drpcDetails.hospitalName}
                    onChange={(e) => setFormData({
                      ...formData,
                      drpcDetails: {...formData.drpcDetails, hospitalName: e.target.value}
                    })}
                  />
                </div>

                <div className="form-group_224">
                  <label>Designation</label>
                  <input
                    type="text"
                    value={formData.drpcDetails.designation}
                    onChange={(e) => setFormData({
                      ...formData,
                      drpcDetails: {...formData.drpcDetails, designation: e.target.value}
                    })}
                  />
                </div>

                <div className="form-group_224">
                  <label>Contact Number</label>
                  <input
                    type="tel"
                    value={formData.drpcDetails.contactNumber}
                    onChange={(e) => setFormData({
                      ...formData,
                      drpcDetails: {...formData.drpcDetails, contactNumber: e.target.value}
                    })}
                  />
                </div>

                <div className="form-group_224">
                  <label>Assigned Districts (comma-separated)</label>
                  <input
                    type="text"
                    value={formData.drpcDetails.assignedDistricts.join(', ')}
                    onChange={(e) => handleDistrictsChange(e.target.value)}
                  />
                  <small className="form-hint_224">
                    Enter multiple districts if this DRPC manages more than one.
                  </small>
                </div>
              </div>

              <div className="modal-actions_224">
                <button
                  type="button"
                  className="btn-secondary_224"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedUser(null);
                    resetForm();
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary_224">
                  <FaEdit /> Update User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedUser && (
        <div className="modal-overlay_224" onClick={() => setShowViewModal(false)}>
          <div className="modal-content_224 view-modal_224" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header_224">
              <h3>DRPC User Details</h3>
              <button className="close-btn_224" onClick={() => setShowViewModal(false)}>
                <FaTimes />
              </button>
            </div>

            <div className="user-details_224">
              <div className="detail-section_224">
                <h4>Personal Information</h4>
                <div className="detail-grid_224">
                  <div className="detail-item_224">
                    <strong>Full Name:</strong>
                    <span>{selectedUser.name}</span>
                  </div>
                  <div className="detail-item_224">
                    <strong>Email:</strong>
                    <span>{selectedUser.email}</span>
                  </div>
                  <div className="detail-item_224">
                    <strong>Mobile:</strong>
                    <span>{selectedUser.profile?.mobile || 'N/A'}</span>
                  </div>
                  <div className="detail-item_224">
                    <strong>Status:</strong>
                    <span className={`status-text_224 ${selectedUser.isActive ? 'active_224' : 'inactive_224'}`}>
                      {selectedUser.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="detail-section_224">
                <h4>District & Hospital Details</h4>
                <div className="detail-grid_224">
                  <div className="detail-item_224">
                    <strong>Primary District:</strong>
                    <span>{selectedUser.drpcDetails?.district}</span>
                  </div>
                  <div className="detail-item_224">
                    <strong>State:</strong>
                    <span>{selectedUser.drpcDetails?.state}</span>
                  </div>
                  <div className="detail-item_224">
                    <strong>Hospital:</strong>
                    <span>{selectedUser.drpcDetails?.hospitalName}</span>
                  </div>
                  <div className="detail-item_224">
                    <strong>Designation:</strong>
                    <span>{selectedUser.drpcDetails?.designation}</span>
                  </div>
                  <div className="detail-item_224">
                    <strong>Contact Number:</strong>
                    <span>{selectedUser.drpcDetails?.contactNumber || 'N/A'}</span>
                  </div>
                  <div className="detail-item_224 full-width_224">
                    <strong>Assigned Districts:</strong>
                    <div className="districts-list_224">
                      {selectedUser.drpcDetails?.assignedDistricts?.map((district, idx) => (
                        <span key={idx} className="district-badge_224">
                          {district}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="detail-section_224">
                <h4>Account Information</h4>
                <div className="detail-grid_224">
                  <div className="detail-item_224">
                    <strong>User ID:</strong>
                    <span className="user-id_224">{selectedUser._id}</span>
                  </div>
                  <div className="detail-item_224">
                    <strong>Created At:</strong>
                    <span>{new Date(selectedUser.createdAt).toLocaleString()}</span>
                  </div>
                  <div className="detail-item_224">
                    <strong>Last Updated:</strong>
                    <span>{new Date(selectedUser.updatedAt).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-actions_224">
              <button
                className="btn-secondary_224"
                onClick={() => {
                  setShowViewModal(false);
                  openEditModal(selectedUser);
                }}
              >
                <FaEdit /> Edit User
              </button>
              <button
                className="btn-secondary_224"
                onClick={() => setShowViewModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageDRPCUsers;